/**
 * widgets/AndonTicker.ts · v351 工厂大屏底部告警条
 *
 * Andon system (丰田生产体系) · 工厂车间挂屏 24/7 显示
 *
 * 显示:
 *   - 加急工单 (red pulse) · WO status='urgent' AND due_date < today
 *   - 已逾期工单 (red flash)
 *   - 客诉处理中 (orange)
 *   - 低库存原料 (yellow)
 *   - 正常运行 (green · 默认底色)
 *
 * 触发条件:
 *   - TV mode 时自动显示 (body[data-tv-mode])
 *   - workshopScreen / productionKanban / bigScreen* 页面也可手动调用
 */
import { query } from '@core/db';

interface AndonItem {
  level: 'crit' | 'warn' | 'ok';
  text: string;
}

function safeQuery<T>(sql: string, params?: unknown[]): T[] {
  try {
    return query<T>(sql, params);
  } catch {
    return [];
  }
}

export function collectAndonItems(): AndonItem[] {
  const items: AndonItem[] = [];
  const today = new Date().toISOString().slice(0, 10);

  // 1. 加急工单
  const urgent = safeQuery<{ c: number }>(`
    SELECT COUNT(*) AS c FROM work_orders
    WHERE COALESCE(priority,'') IN ('urgent', '加急', 'high') AND COALESCE(status,'') NOT IN ('completed','closed','done','已完工')
  `)[0]?.c || 0;
  if (urgent > 0) items.push({ level: 'crit', text: `🔥 加急工单 ${urgent}` });

  // 2. 逾期工单
  const overdue = safeQuery<{ c: number }>(`
    SELECT COUNT(*) AS c FROM work_orders
    WHERE COALESCE(end_date,'') < ? AND COALESCE(status,'') NOT IN ('completed','closed','done','已完工')
  `, [today])[0]?.c || 0;
  if (overdue > 0) items.push({ level: 'crit', text: `⏰ 已逾期 ${overdue}` });

  // 3. 客诉处理中
  const complaint = safeQuery<{ c: number }>(`
    SELECT COUNT(*) AS c FROM complaints WHERE COALESCE(status,'') NOT IN ('closed','done','已处理','resolved')
  `)[0]?.c || 0;
  if (complaint > 0) items.push({ level: 'warn', text: `📢 客诉处理中 ${complaint}` });

  // 4. 低库存原料
  const lowStock = safeQuery<{ c: number }>(`
    SELECT COUNT(*) AS c FROM raw_materials
    WHERE COALESCE(stock_qty,0) < COALESCE(safety_stock,0) AND COALESCE(deleted_at,'')=''
  `)[0]?.c || 0;
  if (lowStock > 0) items.push({ level: 'warn', text: `📦 低库存 ${lowStock}` });

  // 5. 设备故障
  const broken = safeQuery<{ c: number }>(`
    SELECT COUNT(*) AS c FROM equipment WHERE COALESCE(status,'') IN ('breakdown','故障','offline') AND COALESCE(deleted_at,'')='' AND COALESCE(is_active,1)=1
  `)[0]?.c || 0;
  if (broken > 0) items.push({ level: 'crit', text: `⚙️ 设备故障 ${broken}` });

  // 一切正常
  if (items.length === 0) {
    items.push({ level: 'ok', text: '✅ 全部按计划运行 · Tất cả hoạt động bình thường' });
  }

  // 时间戳
  items.push({ level: 'ok', text: `🕐 ${new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}` });

  return items;
}

export function renderAndonTicker(): HTMLElement {
  const items = collectAndonItems();
  const ticker = document.createElement('div');
  ticker.className = 'tn-andon-ticker';
  ticker.id = 'tn-andon-ticker';
  // 双倍 HTML seamless 循环
  const itemsHtml = items.map(it => `<span class="tn-andon-item tn-andon-${it.level}">${escapeHtml(it.text)}</span>`).join('');
  ticker.innerHTML = `<div class="tn-andon-scroll" style="display:flex; gap:0; animation:tnAndonMarquee 60s linear infinite;">${itemsHtml}${itemsHtml}</div>`;
  // 注入 marquee animation
  if (!document.getElementById('tn-andon-style')) {
    const style = document.createElement('style');
    style.id = 'tn-andon-style';
    style.textContent = `@keyframes tnAndonMarquee { 0%{transform:translateX(0);} 100%{transform:translateX(-50%);} }`;
    document.head.appendChild(style);
  }
  return ticker;
}

let mounted = false;
export function mountAndonTicker(): void {
  unmountAndonTicker();
  document.body.appendChild(renderAndonTicker());
  mounted = true;
}

export function unmountAndonTicker(): void {
  const existing = document.getElementById('tn-andon-ticker');
  if (existing) existing.remove();
  mounted = false;
}

export function refreshAndonTicker(): void {
  if (!mounted) return;
  mountAndonTicker();
}

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
